import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppStore } from '@/stores/useAppStore';
import { SmartGoal, HabitTask, Challenge90 } from '@/types';

interface PrefetchStatus {
  isLoading: boolean;
  isComplete: boolean;
  progress: number; // 0-100
  errors: string[];
}

interface PrefetchResult {
  goals?: SmartGoal[];
  habits?: HabitTask[];
  challenges?: Challenge90[];
  notifications?: number;
  badges?: number;
}

/**
 * Hook para pré-carregar dados críticos do usuário de forma inteligente
 * 
 * Estratégia:
 * 1. Carrega dados críticos em paralelo (metas, hábitos, desafios)
 * 2. Prioriza dados mais acessados
 * 3. Usa cache para evitar recarregamentos desnecessários
 * 4. Fornece feedback de progresso
 */
export const useDataPrefetch = (userId: string | null, enabled: boolean = true) => {
  const [status, setStatus] = useState<PrefetchStatus>({
    isLoading: false,
    isComplete: false,
    progress: 0,
    errors: []
  });

  const { setGoals, setHabits, setChallenges } = useAppStore();

  const prefetchData = useCallback(async () => {
    if (!userId || !enabled) {
      return;
    }

    console.log('🚀 Iniciando pré-carregamento inteligente de dados...');
    const startTime = performance.now();

    setStatus({
      isLoading: true,
      isComplete: false,
      progress: 0,
      errors: []
    });

    try {
      // FASE 1: Dados críticos (paralelo) - 70% do progresso
      setStatus(prev => ({ ...prev, progress: 10 }));
      
      const criticalDataPromises = [
        // Metas ativas com mini-goals (LEFT JOIN para incluir metas sem mini-goals)
        supabase
          .from('user_goals')
          .select('*, user_mini_goals (*)')
          .eq('user_id', userId)
          .neq('status', 'concluida')
          .order('created_at', { ascending: false })
          .limit(50),
        
        // Hábitos ativos - TODOS, sem filtro de data (LEFT JOIN para incluir hábitos novos)
        supabase
          .from('user_habits')
          .select('*, habit_checkins(*)')
          .eq('user_id', userId)
          .eq('active', true)
          .order('created_at', { ascending: false })
          .limit(50),
        
        // Desafios ativos não arquivados (LEFT JOIN para incluir desafios sem entradas)
        supabase
          .from('user_challenges')
          .select('*, challenge_journal_entries (*)')
          .eq('user_id', userId)
          .eq('archived', false)
          .eq('active', true)
          .order('created_at', { ascending: false })
          .limit(10)
      ];

      setStatus(prev => ({ ...prev, progress: 30 }));
      
      const [goalsResult, habitsResult, challengesResult] = await Promise.all(criticalDataPromises);
      
      setStatus(prev => ({ ...prev, progress: 70 }));

      const errors: string[] = [];
      const result: PrefetchResult = {};

      // Processar resultados de METAS
      if (goalsResult.error) {
        console.error('❌ Erro ao carregar metas:', goalsResult.error);
        errors.push('Erro ao carregar metas');
      } else {
        const formattedGoals = goalsResult.data?.map((goal: any) => ({
          id: goal.id,
          userId: goal.user_id,
          pillars: goal.pillars || [goal.pilar],
          pillar: goal.pilar,
          title: goal.title,
          specific: goal.specific,
          measurable: goal.measurable,
          achievable: goal.achievable,
          relevant: goal.relevant,
          timeBoundISO: goal.time_bound,
          deadline: goal.deadline,
          status: goal.status,
          createdAt: goal.created_at,
          updatedAt: goal.updated_at,
          miniGoals: goal.user_mini_goals?.map((mini: any) => ({
            id: mini.id,
            goalId: mini.goal_id,
            userId: mini.user_id,
            title: mini.title,
            completed: mini.completed,
            createdAt: mini.created_at
          })) || []
        })) || [];
        
        result.goals = formattedGoals as SmartGoal[];
        setGoals(result.goals);
        console.log('✅ Metas carregadas:', result.goals.length);
        console.log('🔍 Detalhes:', {
          total: result.goals.length,
          primeira_meta: result.goals[0] ? {
            id: result.goals[0].id,
            title: result.goals[0].title,
            miniGoals_count: result.goals[0].miniGoals?.length || 0
          } : 'nenhuma'
        });
        
        if (result.goals.length === 0) {
          console.warn('⚠️ ALERTA: Query de metas retornou 0 resultados');
        }
      }

      // Processar resultados de HÁBITOS
      if (habitsResult.error) {
        console.error('❌ Erro ao carregar hábitos:', habitsResult.error);
        errors.push('Erro ao carregar hábitos');
      } else {
        const formattedHabits = habitsResult.data?.map((habit: any) => ({
          id: habit.id,
          userId: habit.user_id,
          pillars: habit.pillars ?? [habit.pilar],
          pillar: habit.pilar,
          title: habit.title,
          frequency: habit.frequency === 'diaria' ? 'daily' as const : 
                    habit.frequency === 'semanal' ? 'weekly' as const : 
                    habit.frequency === 'mensal' ? 'monthly' as const : 'daily' as const,
          daysOfWeek: habit.days_of_week ?? [],
          specificDates: habit.specific_dates ?? [],
          restDays: habit.rest_days ?? [],
          active: habit.active,
          history: habit.habit_checkins?.map((c: any) => c.date) ?? [],
          createdAt: habit.created_at,
          updatedAt: habit.updated_at,
          motivationWhy: habit.motivation_why,
          identityVoteTarget: habit.identity_vote_target,
          motivationLevel: habit.motivation_level,
          capabilityLevel: habit.capability_level,
          promptType: habit.prompt_type,
          promptTiming: habit.prompt_timing,
          autoAdjustEnabled: habit.auto_adjust_enabled,
          microVersion: habit.micro_version,
          dadosAdicionais: habit.dados_adicionais || {}
        })) ?? [];
        
        // Log de warning se dados inválidos forem detectados
        formattedHabits.forEach(h => {
          if (!h.history) console.warn('⚠️ Hábito sem history:', h.id, h.title);
          if (!h.pillars) console.warn('⚠️ Hábito sem pillars:', h.id, h.title);
        });
        
        result.habits = formattedHabits as HabitTask[];
        setHabits(result.habits);
        console.log('✅ Hábitos carregados:', result.habits.length);
        console.log('🔍 Detalhes:', {
          total: result.habits.length,
          primeiro_habit: result.habits[0] ? {
            id: result.habits[0].id,
            title: result.habits[0].title,
            history_length: result.habits[0].history?.length || 0,
            history_sample: result.habits[0].history?.slice(0, 3)
          } : 'nenhum'
        });
        
        if (result.habits.length === 0) {
          console.warn('⚠️ ALERTA: Query de hábitos retornou 0 resultados');
        }
      }

      // Processar resultados de DESAFIOS
      if (challengesResult.error) {
        console.error('❌ Erro ao carregar desafios:', challengesResult.error);
        errors.push('Erro ao carregar desafios');
      } else {
        const formattedChallenges = challengesResult.data?.map((challenge: any) => ({
          id: challenge.id,
          userId: challenge.user_id,
          habitTitle: challenge.habit_title || 'Desafio 90 Dias',
          habitDescription: challenge.habit_description || '',
          frequency: 'daily' as const,
          hour: challenge.hour,
          startISO: challenge.start_date,
          streak: challenge.streak,
          bestStreak: challenge.best_streak || 0,
          totalDays: challenge.completed_dates?.length || 0,
          completedDates: challenge.completed_dates ?? [],
          status: challenge.active ? 'ativo' as const : 'concluido' as const,
          archived: challenge.archived || false,
          createdAt: challenge.created_at,
          updatedAt: challenge.updated_at,
          journalEntries: challenge.challenge_journal_entries?.map((entry: any) => ({
            date: entry.date,
            text: entry.reflection || '',
            learning: entry.learning || '',
            gratitude: entry.gratitude || '',
            photoUrl: entry.photo_url,
            mood: entry.mood,
            createdAt: entry.created_at
          })) ?? []
        })) || [];
        
        result.challenges = formattedChallenges as Challenge90[];
        setChallenges(result.challenges);
        console.log('✅ Desafios carregados:', result.challenges.length);
        console.log('🔍 Detalhes:', {
          total: result.challenges.length,
          primeiro_desafio: result.challenges[0] ? {
            id: result.challenges[0].id,
            title: result.challenges[0].habitTitle,
            completedDates_count: result.challenges[0].completedDates?.length || 0,
            journalEntries_count: result.challenges[0].journalEntries?.length || 0
          } : 'nenhum'
        });
        
        if (result.challenges.length === 0) {
          console.warn('⚠️ ALERTA: Query de desafios retornou 0 resultados');
        }
      }

      // VERIFICAÇÃO DE INTEGRIDADE
      const integrityCheck = {
        habitsLoaded: result.habits?.length || 0,
        goalsLoaded: result.goals?.length || 0,
        challengesLoaded: result.challenges?.length || 0,
        totalItems: (result.habits?.length || 0) + (result.goals?.length || 0) + (result.challenges?.length || 0)
      };

      console.log('🔍 Verificação de Integridade:', integrityCheck);

      // Alerta se nenhum dado foi carregado
      if (integrityCheck.totalItems === 0 && errors.length === 0) {
        console.warn('⚠️ CRÍTICO: Nenhum dado foi carregado mas não houve erros reportados!');
        console.warn('   Usuário pode não ter criado nenhum dado ainda OU há problema nos filtros');
        errors.push('Nenhum dado disponível - verificar se usuário já criou hábitos/metas');
      }

      // FASE 2: Dados secundários (non-blocking) - 30% do progresso
      setStatus(prev => ({ ...prev, progress: 80 }));
      
      // Carregar contadores sem bloquear
      Promise.all([
        // Contagem de notificações não lidas
        supabase
          .from('user_notifications')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .is('read_at', null),
        
        // Contagem de badges conquistados
        supabase
          .from('user_badges')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
      ]).then(([notifResult, badgesResult]) => {
        if (!notifResult.error) {
          result.notifications = notifResult.count || 0;
          console.log('✅ Notificações não lidas:', result.notifications);
        }
        if (!badgesResult.error) {
          result.badges = badgesResult.count || 0;
          console.log('✅ Badges conquistados:', result.badges);
        }
      }).catch(err => {
        console.warn('⚠️ Erro ao carregar dados secundários (não crítico):', err);
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ PRÉ-CARREGAMENTO COMPLETO');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`⏱️  Tempo total: ${duration}ms`);
      console.log('📊 Dados carregados:');
      console.log(`   - Metas: ${result.goals?.length || 0}`);
      console.log(`   - Hábitos: ${result.habits?.length || 0}`);
      console.log(`   - Desafios: ${result.challenges?.length || 0}`);
      console.log(`   - Notificações: ${result.notifications || 0}`);
      console.log(`   - Badges: ${result.badges || 0}`);
      if (errors.length > 0) {
        console.log('⚠️  Erros:', errors.join(', '));
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      setStatus({
        isLoading: false,
        isComplete: true,
        progress: 100,
        errors
      });

      return result;
    } catch (error) {
      console.error('❌ Erro crítico no pré-carregamento:', error);
      
      setStatus({
        isLoading: false,
        isComplete: false,
        progress: 0,
        errors: ['Erro crítico no carregamento']
      });

      throw error;
    }
  }, [userId, enabled, setGoals, setHabits, setChallenges]);

  // Auto-executar quando userId estiver disponível
  useEffect(() => {
    if (userId && enabled && !status.isComplete && !status.isLoading) {
      prefetchData();
    }
  }, [userId, enabled, prefetchData, status.isComplete, status.isLoading]);

  return {
    ...status,
    refetch: prefetchData
  };
};

/**
 * Hook simplificado para verificar se os dados já foram pré-carregados
 */
export const useIsPrefetchComplete = () => {
  const goals = useAppStore(state => state.goals);
  const habits = useAppStore(state => state.habits);
  const challenges = useAppStore(state => state.challenges);
  
  // Considera completo se pelo menos um dos arrays foi populado
  return goals.length > 0 || habits.length > 0 || challenges.length > 0;
};
